/**
 * Authored quest chains — the written half of the Expedition quest system.
 *
 * Pure data. No logic, no scaling, no generation: `quests.ts` owns all of that, and the **server**
 * owns every counter. A chain's five steps always run in order; chains themselves are handed out
 * in random order across sessions, so every brief orients in one clause before it asks.
 *
 * Text contract:
 * - `objective` always contains `{n}` (the scaled goal) and contains `{area}` only when the step
 *   carries an `areaHint`.
 * - `brief` is exactly two spoken lines. `line` is the resolution, spoken by the same voice.
 * - Step 5 of every chain is the set piece.
 */
import { EntityKind } from './protocol.js';
import {
  QuestKind,
  SalvageTarget,
  type QuestChain,
  type VoiceDef,
  type VoiceId,
} from './quests.js';

export const VOICES: Record<VoiceId, VoiceDef> = {
  isolde: {
    id: 'isolde',
    name: 'Isolde Vane',
    role: 'Lanternkeeper of Lanternfall',
    tone: 'Quiet, precise, unbearable hope. Counts the town in lanterns. Never says the word.',
  },
  sabel: {
    id: 'sabel',
    name: 'Quartermaster Sabel Roque',
    role: 'Lantern Compact quartermaster',
    tone: 'Dry. Speaks in line items and rates. Keeps every feeling inside the arithmetic.',
  },
  wren: {
    id: 'wren',
    name: 'Ferryman Wren',
    role: 'Ferry runner, fixer, seller of passage',
    tone: 'Warm and charming, lies fluently. Every promise carries a hedge you hear later.',
  },
  ott: {
    id: 'ott',
    name: 'Deacon Ott',
    role: 'Deacon of the Hymnal',
    tone: 'Reverent, certain, faintly hungry. Says the Choir, and calls killing quieting.',
  },
  pell: {
    id: 'pell',
    name: 'Pell',
    role: 'A child in Lanternfall, maybe nine',
    tone: 'Short flat sentences. Literal. Brave in a way that is hard to read.',
  },
  choir: {
    id: 'choir',
    name: 'The Choir',
    role: 'What is left of the song, still singing',
    tone: 'Plural first person, liturgical. Gets your name slightly wrong and does not notice.',
  },
};

export const QUEST_CHAINS: QuestChain[] = [
  // ---------------------------------------------------------------- 1. hallo
  {
    id: 'hallo',
    title: 'The Long Hallo',
    voice: 'isolde',
    twist:
      'The thing answering Isolde on the open channel is her son Aubrey. He is a Chorister now, and he still knows his name.',
    steps: [
      {
        id: 'hallo_1',
        kind: 3,
        title: "Aubrey's Effects",
        voice: 'isolde',
        brief: [
          'I keep a lantern lit for him. Three hundred nights now, and the wick is the only thing that has changed.',
          'His locker was Barrow side, row eleven. Bring me what is in it and I will not ask you for anything else today.',
        ],
        objective: 'Open {n} caches',
        line: 'Row eleven had a comb, a tin whistle, and one boot. I have lit a second lantern. Thank you.',
        goal: 4,
        target: 0,
      },
      {
        id: 'hallo_2',
        kind: 1,
        title: 'They Come Closer',
        voice: 'isolde',
        brief: [
          'I broadcast on the open channel every dusk. Something out there has started walking toward the sound.',
          'They gather on the road below my line. Push them back before they learn the way in.',
        ],
        objective: 'Kill {n} Choristers in {area}',
        line: 'One of them stopped when I said his name into the channel. It did not turn. It stopped.',
        goal: 6,
        target: 1,
        areaHint: 'the Lantern Road',
      },
      {
        id: 'hallo_3',
        kind: 5,
        title: 'Cut the Answer',
        voice: 'isolde',
        brief: [
          'There is a Cantor that sings back on my channel. The same six notes, every dusk, since the winter.',
          'Take it while its chest is bright. I want to hear what is underneath it.',
        ],
        objective: 'Kill {n} Cantors mid-song',
        line: 'Underneath it there was a word. Two syllables. The second one goes up at the end.',
        goal: 1,
      },
      {
        id: 'hallo_4',
        kind: 2,
        title: 'Row Eleven',
        voice: 'choir',
        brief: [
          'Waker. Wakers. You are one and we have counted you as many, and we are sorry for the arithmetic.',
          'A lantern at row eleven has burned three hundred nights. Come and stand in it. We kept the berth.',
        ],
        objective: 'Clear {n} Hymns out of {area}',
        line: "The berth was made up, fresh. Under the pillow, a congregant's tag. VANE, A. Barrow 11-C.",
        goal: 5,
        areaHint: 'the Tithe Barrows',
      },
      {
        id: 'hallo_5',
        kind: 4,
        title: 'The Long Hallo',
        voice: 'isolde',
        brief: [
          'He answered. Three words, in his own voice, and one of them was my name.',
          'Go to row eleven and stand there until he comes to you. Then put the lantern out. Please put it out.',
        ],
        objective: 'Hold {area} for {n} seconds',
        line: 'I took his lantern down from the window tonight. Thirty others still burning. Come and I will name them.',
        goal: 90,
        areaHint: 'the Tithe Barrows',
        setPiece: true,
      },
    ],
  },

  // --------------------------------------------------------------- 2. ledger
  {
    id: 'ledger',
    title: 'The Quiet Ledger',
    voice: 'sabel',
    twist:
      "The Compact's shrinkage column is a list of Wakers it wrote off while they were still alive, and Sabel signed every entry.",
    steps: [
      {
        id: 'ledger_1',
        kind: 3,
        title: 'Reconcile the Chapel',
        voice: 'sabel',
        brief: [
          'Manifest says forty crates went into that chapel in spring. Thirty-one came back out. I do not like the nine.',
          'Open what you find and bring me the seal numbers. I will do the subtraction here, where it is warm.',
        ],
        objective: 'Open {n} caches',
        line: 'Four seals, four numbers, none of them on my inbound sheet. So the nine are still nine, plus four.',
        goal: 4,
        target: 0,
      },
      {
        id: 'ledger_2',
        kind: 2,
        title: 'Clear My Column',
        voice: 'sabel',
        brief: [
          'I cannot count a room that is still moving. Every tally I take off that chapel comes back wrong by two.',
          'Empty it. Standard rate plus hazard, paid in Wick and not in Compact credit, which I know you prefer.',
        ],
        objective: 'Clear {n} Hymns out of {area}',
        line: 'Room is still, tally holds. The nine crates are logged as shrinkage, in my hand, dated before they shipped.',
        goal: 4,
        areaHint: 'the Drowned Chapel',
      },
      {
        id: 'ledger_3',
        kind: 3,
        title: 'The Shrinkage Line',
        voice: 'sabel',
        brief: [
          'Shrinkage is what we call a loss we cannot itemise. It is a mercy word. It costs the Compact nothing.',
          'Bring me three shards off that chapel floor. I would like to know what the nine turned into.',
        ],
        objective: 'Carry {n} Hymn Shards out',
        line: 'Assay came back carbon, bell-bronze, bone. Bone is not a line item. I am going to have to open one.',
        goal: 3,
        target: 1,
      },
      {
        id: 'ledger_4',
        kind: 1,
        title: 'Written Off',
        voice: 'sabel',
        brief: [
          'Nine names. Cotter, Adeyemi, Frost, and six more I have said out loud so often they stopped being people.',
          'They were laid in the Barrows. Go and count what is walking there. Then come back and I will read you the rest.',
        ],
        objective: 'Kill {n} Choristers in {area}',
        line: "Seven wore Compact issue. Cotter's coat had a write-off chit in the pocket. Initials S.R. That is me.",
        goal: 7,
        target: 1,
        areaHint: 'the Tithe Barrows',
      },
      {
        id: 'ledger_5',
        kind: 1,
        title: 'Close the Books',
        voice: 'sabel',
        brief: [
          'Three heavy ones in the chapel now. I signed for all three of them back when they had berth numbers.',
          'I am not asking you to forgive the arithmetic. I am asking you to close it. Full rate, paid before you go.',
        ],
        objective: 'Kill {n} Sextons in {area}',
        line: 'Ledger balances. Forty out of forty. I have written my own name into the shrinkage column and left it wet.',
        goal: 3,
        target: 3,
        areaHint: 'the Drowned Chapel',
        setPiece: true,
      },
    ],
  },

  // ---------------------------------------------------------------- 3. relay
  {
    id: 'relay',
    title: 'What the Relay Sings',
    voice: 'ott',
    twist:
      'The relay sings to whoever carries it. Ott knew it would; he sent you because a convert is worth more to the Hymnal than a courier.',
    steps: [
      {
        id: 'relay_1',
        kind: 1,
        title: 'Clear the Nave',
        voice: 'ott',
        brief: [
          'There is a relay on the Choir Stones. Brass housing, about the size of a lamp. The Hymnal would like it back.',
          'The Choir keeps a few voices standing around it. Quiet them, and the walk in gets a great deal shorter.',
        ],
        objective: 'Kill {n} Choristers in {area}',
        line: 'You quieted five and the deck did not get quieter. Note that. Then go on.',
        goal: 5,
        target: 1,
        areaHint: 'the Choir Stones',
      },
      {
        id: 'relay_2',
        kind: 1,
        title: 'A Clear Sky',
        voice: 'ott',
        brief: [
          'The relay needs open air to lift. The orchard has open air, and four tall singers perched along the rows of it.',
          'Fold them. Then the mast is only a walk, and I will meet you at the hill with the harness.',
        ],
        objective: 'Kill {n} Cantors in {area}',
        line: 'Four folded, and the orchard hums lower now. Four voices out of the chord. You did that with your hands.',
        goal: 4,
        target: 2,
        areaHint: 'the Hollow Orchard',
      },
      {
        id: 'relay_3',
        kind: 3,
        title: 'Give It Voice',
        voice: 'ott',
        brief: [
          'The housing is intact. The voice is not. It wants three shards seated in the brass before it will lift.',
          'Carry them out in your hands. Do not pouch them, do not wrap them. They prefer a hand.',
        ],
        objective: 'Carry {n} Hymn Shards out',
        line: 'You held them the whole way. And they warmed. They only do that for some people.',
        goal: 3,
        target: 1,
      },
      {
        id: 'relay_4',
        kind: 5,
        title: 'Two Voices Short',
        voice: 'ott',
        brief: [
          'One more thing before the mast. The relay tunes to a live chord, and two singers have to come out of it.',
          'Take them mid-song, while the spine is bright. And listen while you do it. I insist on the listening.',
        ],
        objective: 'Kill {n} Cantors mid-song',
        line: 'It used your word back at you. The one you said when the first one dropped. It is learning you.',
        goal: 2,
      },
      {
        id: 'relay_5',
        kind: 4,
        title: 'What the Relay Sings',
        voice: 'ott',
        brief: [
          'Up the mast, seat it, and stand under it while it finds the chord. Seventy-five seconds and no longer.',
          'I could have sent a courier with a cart. I sent you, and I sent you with your hands free. That was chosen.',
        ],
        objective: 'Hold {area} for {n} seconds',
        line: 'It sang your name for a minute and a half and you never put it down. The Hymnal keeps a berth for you.',
        goal: 75,
        areaHint: 'the Watch Hill',
        setPiece: true,
      },
    ],
  },

  // ------------------------------------------------------------- 4. manifest
  {
    id: 'manifest',
    title: 'Manifest',
    voice: 'wren',
    twist:
      "The Ferry's crates are coffins and the freight has always been people. Wren has been selling a passage nightward that he never once ran.",
    steps: [
      {
        id: 'manifest_1',
        kind: 3,
        title: 'Four Crates Out',
        voice: 'wren',
        brief: [
          'Friend. Four crates of mine went into the Downs on the last run and only the paperwork came back out.',
          'Pop them, confirm the seals, and I will see you right. Twelve percent, and I round up. Always have.',
        ],
        objective: 'Open {n} caches',
        line: 'All four seals matched, good. Do not open the linings. They are packed to a spec and I would know.',
        goal: 4,
        target: 0,
      },
      {
        id: 'manifest_2',
        kind: 1,
        title: 'Clear the Lane',
        voice: 'wren',
        brief: [
          'The lane from the chapel out to the road has gone busy, and my people will not push a cart through busy.',
          'Six of them, roughly. Thin them and the whole run is a stroll. I will have tea on at the knoll.',
        ],
        objective: 'Kill {n} Choristers in {area}',
        line: 'Lane is open. And no, you did not hear knocking. Cargo settles. Cold metal, long drop, it settles.',
        goal: 6,
        target: 1,
        areaHint: 'the Drowned Chapel',
      },
      {
        id: 'manifest_3',
        kind: 2,
        title: 'A Quiet Road',
        voice: 'wren',
        brief: [
          'The road is the last stretch, and the last stretch is where insurance gets expensive for me.',
          'Sweep it. Anything standing between the breach and the hill, put it down. I am good for the cost.',
        ],
        objective: 'Clear {n} Hymns out of {area}',
        line: 'Road is quiet. One crate was stencilled on the inside. 41-D. That is a berth number, and I would let it lie.',
        goal: 4,
        areaHint: 'the Lantern Road',
      },
      {
        id: 'manifest_4',
        kind: 4,
        title: 'Wait for the Boat',
        voice: 'wren',
        brief: [
          'Stack them on the hill and hold that ground a minute. The pickup is punctual when the pickup is paid.',
          'Do not read the linings. I know you want to. It is a manifest, not a story, and it is not your story.',
        ],
        objective: 'Hold {area} for {n} seconds',
        line: 'No boat came. You read the lining anyway. Berth 41-D, and under it in pencil: MARA COTTER, DEPARTING.',
        goal: 60,
        areaHint: 'the Watch Hill',
      },
      {
        id: 'manifest_5',
        kind: 1,
        title: 'Read the Linings',
        voice: 'wren',
        brief: [
          'All right. All right. The berths were the freight and the freight was people, and they paid me up front.',
          'Three of them are still in the Barrows wearing their crate numbers. Go and finish what I charged for.',
        ],
        objective: 'Kill {n} Sextons in {area}',
        line: '41-D, 41-E, 44-A. I have a list of two hundred more, and I have already sold nineteen of them a berth.',
        goal: 3,
        target: 3,
        areaHint: 'the Tithe Barrows',
        setPiece: true,
      },
    ],
  },

  // ----------------------------------------------------------------- 5. pell
  {
    id: 'pell',
    title: "Pell's Father",
    voice: 'pell',
    twist:
      "The Sexton on the Choir Stones is wearing Pell's father's coat, and the boy's name tape is still sewn inside it.",
    steps: [
      {
        id: 'pell_1',
        kind: 3,
        title: "My Dad's Tools",
        voice: 'pell',
        brief: [
          'My dad went in on a Tuesday. He took the green toolroll. It has his initials burned into the flap.',
          'You open boxes in there. Open three. Look for the green one. I can pay. I have forty Wick saved.',
        ],
        objective: 'Open {n} caches',
        line: 'Not it. That is okay. There are more boxes. I will save more Wick.',
        goal: 3,
        target: 0,
      },
      {
        id: 'pell_2',
        kind: 1,
        title: 'Where He Slept',
        voice: 'pell',
        brief: [
          'He slept in the Barrows when he stayed over. Row nine. He said the berths were cut like shelves in the earth.',
          'There are five walking around there now. Isolde says they are not people. Kill them so I can go look.',
        ],
        objective: 'Kill {n} Choristers in {area}',
        line: 'None of them were tall enough. My dad is very tall. I checked with my hand on the door frame.',
        goal: 5,
        target: 1,
        areaHint: 'the Tithe Barrows',
      },
      {
        id: 'pell_3',
        kind: 5,
        title: 'He Used to Sing',
        voice: 'pell',
        brief: [
          'My dad sang when he worked. Low, with no words in it. Mum said it was the worst noise in Lanternfall.',
          'There is one out there doing it. Make it stop so I can stop listening for it.',
        ],
        objective: 'Kill {n} Cantors mid-song',
        line: 'That was not him. I would know. But you have to check every one, so keep checking.',
        goal: 1,
      },
      {
        id: 'pell_4',
        kind: 2,
        title: 'The Loud Room',
        voice: 'pell',
        brief: [
          'Isolde says do not go to the Choir Stones. She says it the way grown-ups say a thing they actually mean.',
          'So it is there. Clear it out. I will wait by the gate and I will not tell her you went.',
        ],
        objective: 'Clear {n} Hymns out of {area}',
        line: 'You said there is a big one left. You said it is wearing a green coat. My dad has a green coat.',
        goal: 5,
        areaHint: 'the Choir Stones',
      },
      {
        id: 'pell_5',
        kind: 1,
        title: "Pell's Father",
        voice: 'pell',
        brief: [
          'It is him. I know it is him. That is his coat and I sewed the name tape into it myself, badly.',
          'I am not coming. You go. And do not tell me it was quick, because I will know you made that up.',
        ],
        objective: 'Kill {n} Sextons in {area}',
        line: 'The tape inside says PELL, in green thread, crooked. He kept it on. He kept it on the whole time.',
        goal: 3,
        target: 3,
        areaHint: 'the Choir Stones',
        setPiece: true,
      },
    ],
  },

  // ---------------------------------------------------------------- 6. doubt
  {
    id: 'doubt',
    title: "The Deacon's Doubt",
    voice: 'ott',
    twist:
      'Played at quarter speed, the Cantor song Ott has been setting to liturgy for nine years is a man begging.',
    steps: [
      {
        id: 'doubt_1',
        kind: 5,
        title: 'One Clean Verse',
        voice: 'ott',
        brief: [
          'The liturgy is short a verse and I have permission to lengthen it. I need one clean recording.',
          'Take a Cantor at the top of its song. The slate in your kit will hold the whole of it.',
        ],
        objective: 'Kill {n} Cantors mid-song',
        line: 'Clean. Eleven seconds, no wind on it. I will have it copied out and sung at dusk by Thursday.',
        goal: 1,
      },
      {
        id: 'doubt_2',
        kind: 1,
        title: 'Thin the Chord',
        voice: 'ott',
        brief: [
          'The orchard choir sings in five and I cannot hear the parts. A chord will not sit still to be transcribed.',
          'Bring it down to nothing and I will take them one at a time next week. All five of them, please.',
        ],
        objective: 'Kill {n} Cantors in {area}',
        line: 'Five separate voices on the slate. Played together they are a hymn. Played apart, four of them repeat.',
        goal: 5,
        target: 2,
        areaHint: 'the Hollow Orchard',
      },
      {
        id: 'doubt_3',
        kind: 5,
        title: 'Slow It Down',
        voice: 'ott',
        brief: [
          'I put the slate at quarter speed last night. That is a thing a careful man does, and I am careful.',
          'Get me two more, taken mid-song, so I can be certain the first one was the slate and not the singer.',
        ],
        objective: 'Kill {n} Cantors mid-song',
        line: 'Both the same at quarter speed. It is not the slate. There are words in it, and they are in the old tongue.',
        goal: 2,
      },
      {
        id: 'doubt_4',
        kind: 2,
        title: 'What It Says',
        voice: 'ott',
        brief: [
          'I have had the quarter-speed running since second bell. I have not written anything down yet.',
          'Clear the orchard. All of it. I will not have another one singing while I am trying to hear this one.',
        ],
        objective: 'Clear {n} Hymns out of {area}',
        line: "It says please. It says a woman's name, then please, forty-one times, and then it starts over.",
        goal: 4,
        areaHint: 'the Hollow Orchard',
      },
      {
        id: 'doubt_5',
        kind: 4,
        title: "The Deacon's Doubt",
        voice: 'ott',
        brief: [
          'The whole liturgy sits on the Choir Stones and it runs eighty seconds. I have sung it at dusk for nine years.',
          'Stand inside it and let the slate take all of it. Then I will know what my mouth has been doing.',
        ],
        objective: 'Hold {area} for {n} seconds',
        line: "Nine years. Eighty seconds a night. I have been teaching children to beg in a dead man's voice.",
        goal: 80,
        areaHint: 'the Choir Stones',
        setPiece: true,
      },
    ],
  },

  // ---------------------------------------------------------------- 7. tithe
  {
    id: 'tithe',
    title: 'The Tithe',
    voice: 'sabel',
    twist:
      'The Compact and the Ferry are selling each other the same sealed crates in a loop; the only cargo that actually moves is the courier.',
    steps: [
      {
        id: 'tithe_1',
        kind: 1,
        title: 'Standard Rate',
        voice: 'sabel',
        brief: [
          'New arrangement. The Compact pays a tithe on cleared ground and the Ferry pays a bounty on the same ground.',
          'Five Choristers, anywhere in the Downs. Both parties have signed. I have never seen them agree before.',
        ],
        objective: 'Kill {n} Choristers',
        line: 'Five confirmed. Both parties paid in full, the same evening, and neither asked to see the other receipt.',
        goal: 5,
        target: 1,
      },
      {
        id: 'tithe_2',
        kind: 3,
        title: 'The Same Cargo',
        voice: 'wren',
        brief: [
          'Sabel does not know I am on this channel, and let us keep that arrangement. It is working beautifully.',
          'Three shards. Sell them to her at Compact rate, then sell me her invoice. I pay for paper, not for rocks.',
        ],
        objective: 'Carry {n} Hymn Shards out',
        line: 'She bought the shards. I bought the invoice. Someone sold her these same shards last month. Who.',
        goal: 3,
        target: 1,
      },
      {
        id: 'tithe_3',
        kind: 1,
        title: 'Eight on the Road',
        voice: 'sabel',
        brief: [
          'Eight on the road, and the Ferry filed the same eight on their sheet before you had raised a hand to any of them.',
          'Go anyway. The rate is good and I have stopped pretending I know where the rate is coming from.',
        ],
        objective: 'Kill {n} Choristers in {area}',
        line: 'Eight down, and the crate at the bend in the road is the one you opened last week. Seal intact. Reprinted.',
        goal: 8,
        target: 1,
        areaHint: 'the Lantern Road',
      },
      {
        id: 'tithe_4',
        kind: 2,
        title: 'Both Sides Paid',
        voice: 'sabel',
        brief: [
          'I pulled the loop apart. Ferry buys from Compact, Compact buys from Ferry, four times a season, same crates.',
          'Clear the hill so I can weigh them myself. If the seals are reprinted then we are not selling salvage.',
        ],
        objective: 'Clear {n} Hymns out of {area}',
        line: 'They weigh eleven kilos empty and eleven kilos full. The only thing that moves in this loop is you.',
        goal: 5,
        areaHint: 'the Watch Hill',
      },
      {
        id: 'tithe_5',
        kind: 4,
        title: 'The Tithe',
        voice: 'wren',
        brief: [
          'Both of us are coming up through the orchard for the same handover, and neither of us intends to pay the other one.',
          'Hold the rail car seventy-five seconds. Whoever is standing there when it arrives is what gets shipped.',
        ],
        objective: 'Hold {area} for {n} seconds',
        line: 'Rail car came empty and left with a manifest. One line on it. Weight, eighty kilos. Description, freight.',
        goal: 75,
        areaHint: 'the Hollow Orchard',
        setPiece: true,
      },
    ],
  },

  // ------------------------------------------------------------- 8. stillsun
  {
    id: 'stillsun',
    title: 'The Stillsun Hour',
    voice: 'isolde',
    twist:
      'For the doused hour the Choir remembers being alive and asks, sincerely, to be let inside Lanternfall.',
    steps: [
      {
        id: 'stillsun_1',
        kind: 3,
        title: 'Oil for the Hour',
        voice: 'isolde',
        brief: [
          'Once a day we put every lantern out for an hour. It is how we learned the Hymns find us by the light.',
          'The hour is coming and I am nine flasks short. Open three caches and bring me anything that burns.',
        ],
        objective: 'Open {n} caches',
        line: 'Enough for thirty-one wicks and one spare. I will keep the spare on my sill. Old habit.',
        goal: 3,
        target: 0,
      },
      {
        id: 'stillsun_2',
        kind: 1,
        title: 'Before the Dousing',
        voice: 'isolde',
        brief: [
          'They walk the road in the dark hour. Six of them, always six, always the same distance from our gate.',
          'Meet them out there before we go dark. I would rather it happened where nobody at home can hear.',
        ],
        objective: 'Kill {n} Choristers in {area}',
        line: 'They stopped at the lantern line and waited. They did not cross it. They have never once crossed it.',
        goal: 6,
        target: 1,
        areaHint: 'the Lantern Road',
      },
      {
        id: 'stillsun_3',
        kind: 5,
        title: 'Two Kept Singing',
        voice: 'isolde',
        brief: [
          'In the dark hour the singing changes. It goes slow and low, the way you sing to somebody already asleep.',
          'Two of them are doing it tonight. Cut them mid-verse. I want one dousing where I do not hear that.',
        ],
        objective: 'Kill {n} Cantors mid-song',
        line: 'The second one used my given name. Not the channel name. The one my mother used, three hundred years back.',
        goal: 2,
      },
      {
        id: 'stillsun_4',
        kind: 1,
        title: 'Let Us In',
        voice: 'choir',
        brief: [
          'Waker. Wakers. We are unsure which of you is the one, and we have been unsure for three hundred nights.',
          'In the dark hour we remember hands. Five of us are on the hill and will not fight. Come and be sure.',
        ],
        objective: 'Kill {n} Cantors in {area}',
        line: 'None of them raised a spine. They folded when you came close, and the last one said: open the gate.',
        goal: 5,
        target: 2,
        areaHint: 'the Watch Hill',
      },
      {
        id: 'stillsun_5',
        kind: 4,
        title: 'The Stillsun Hour',
        voice: 'isolde',
        brief: [
          'The lanterns go out at fourth bell. There are thirty-one of us behind that line and there is one gate.',
          'Stand on the line for the whole hour. They will ask to come in. Do not answer them. Not once.',
        ],
        objective: 'Hold {area} for {n} seconds',
        line: 'They asked forty times and you did not answer. I relit thirty-one lanterns at dawn. The spare stays out.',
        goal: 90,
        areaHint: 'the Lantern Road',
        setPiece: true,
      },
    ],
  },
];

// ------------------------------------------------------------------ The Waking

/**
 * The Waking — the six-step tutorial chain, and the only `scripted` chain in the library.
 *
 * Deliberately **not** a member of `QUEST_CHAINS`: that array is the Downs' quest library,
 * every step of it is rolled at random and scaled by party size, and this one is a script that
 * must run in order, on one map, for one player, paying nothing. `chainsForMap` is the seam that
 * keeps the two apart; `mapId` is what it reads.
 *
 * The order teaches the five verbs a Waker needs and nothing else: walk, sprint-and-mantle,
 * fight, loot, use, leave. Isolde talks you through all six because the tutorial is the one place
 * in STILLSUN where somebody is definitely listening.
 */
export const WAKING_CHAIN: QuestChain = {
  id: 'waking',
  title: 'The Waking',
  voice: 'isolde',
  mapId: 'yard',
  scripted: true,
  twist: 'There is no twist. It is the one hour on the Downs where nothing is being kept from you.',
  steps: [
    {
      id: 'waking_1',
      kind: QuestKind.Reach,
      title: 'Stand Up',
      voice: 'isolde',
      // She is standing at the gate, at the far end of this yard, while she says it — see
      // YARD's `npcs`. So the writing is a woman in the open air behind you, not a radio: the
      // spawn is at z = 4 facing -Z and she is at z = 16.4, which makes her a turn of the head
      // away and puts the pad this step wants squarely in front.
      brief: [
        'You are awake, then. I am the woman at the gate with the lantern — thirty-one of us behind me, and yours is not lit yet.',
        'Do not thank me for it. There is a lit pad on the yard floor ahead of you: walk onto it, and that is the whole of this one.',
      ],
      objective: 'Walk to {area}',
      line: 'Good. You can walk. Two hundred of you never got that far, and I counted every one.',
      goal: 1,
      areaHint: 'the Waking Platform',
    },
    {
      id: 'waking_2',
      kind: QuestKind.Reach,
      title: 'Up Is a Direction',
      voice: 'isolde',
      brief: [
        'The Downs is stacked here. Everything worth having is above the thing that wants to kill you.',
        'Hold your sprint, run at the stepped crates and pull yourself up. Your hands know how.',
      ],
      objective: 'Climb onto {area}',
      line: 'That is a mantle. Sprint into a ledge and you go over it, every time, for the rest of your life.',
      goal: 1,
      areaHint: 'the Stepped Platform',
    },
    {
      id: 'waking_3',
      kind: QuestKind.Hunt,
      title: 'Three Voices',
      voice: 'isolde',
      brief: [
        'Three of them came over the low wall in the night. They are Choristers. They were the living, once.',
        'You will not talk them down and you will not outlive them. Put all three of them out.',
      ],
      objective: 'Kill {n} Choristers',
      line: 'Three. They stop singing when they go down, and you will never stop noticing that they do.',
      goal: 3,
      target: EntityKind.Chorister,
    },
    {
      id: 'waking_4',
      kind: QuestKind.Salvage,
      title: 'What They Left',
      voice: 'isolde',
      brief: [
        'There is a Compact crate against the far wall with a seal nobody has bothered to break.',
        'Open it and take what is inside. Everything you own, you took off something that stopped moving.',
      ],
      objective: 'Open {n} crate',
      line: 'A stim and a tin of brass. That is a good hour on the Downs, and I am not being kind.',
      goal: 1,
      target: SalvageTarget.Cache,
    },
    {
      id: 'waking_5',
      kind: QuestKind.Use,
      title: 'Burns Going In',
      voice: 'isolde',
      brief: [
        'The tallow stim is the difference between a bad night and a name on my window. Learn it now.',
        'Take it out and use it, here, where nothing is shooting at you. It burns. That is how you know.',
      ],
      objective: 'Use {n} Tallow Stim',
      line: 'It burns for four seconds and then you are forty better off. Carry two. Always carry two.',
      goal: 1,
      itemId: 'med_stim',
    },
    {
      id: 'waking_6',
      kind: QuestKind.Extract,
      title: 'Come Home',
      voice: 'isolde',
      // The rung is still `QuestKind.Extract` — it completes the instant extraction resolves,
      // and it does. What changed is *how* extraction resolves in the tutorial: not by standing
      // in the beacon, but by pressing E on Isolde, who is beside it. A first extraction should
      // be somebody letting you in, not a circle on the floor with a timer.
      brief: [
        'Last thing, and it is the only one that has ever mattered: the way out is a person, and you walk to her.',
        'I am at the gate, beside the Ferry light, where I have been all morning. Come and say it to my face.',
      ],
      objective: 'Speak to Isolde at the Ferry',
      line: 'You came back, and I have your name for my window. Thirty-two lanterns tonight. Lanternfall is open to you.',
      goal: 1,
      areaHint: 'the Ferry',
      setPiece: true,
    },
  ],
};

/**
 * The chain library a map draws from. The Yard runs The Waking and nothing else; every other
 * ground runs the Downs library, which The Waking is never a member of.
 */
export function chainsForMap(mapId: string): readonly QuestChain[] {
  if (WAKING_CHAIN.mapId === mapId) return [WAKING_CHAIN];
  return QUEST_CHAINS.filter((c) => c.mapId === undefined || c.mapId === mapId);
}
